import React, { FunctionComponent, useState } from 'react';
import { Badge, ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import moment from 'moment';

import { IUser } from '../../../../../../store/UserSlice';
import {
  getFormatedDurationByMinutes,
  getShortName,
} from '../../../../../../helper';
import { IPaymentHistory } from '../../../../../../store/V2/PaymentHistorySlice';
import { IInvoice } from '../../../../../../store/V2/InvoicesSlice';
import { ICourse } from '../../../../../../store/V2/CoursesSlice';
import { IFlexiblePlan } from '../../../../../../store/V2/FlexiblePlansSlice';
import { ISubscriptionPlan } from '../../../../../../store/V2/SubscriptionPlansSlice';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';

interface PaymentHistoryTableRowProps {
  paymentHistory: IPaymentHistory;
  viewAdminNotes: () => void;
}

const PaymentHistoryTableRow: FunctionComponent<PaymentHistoryTableRowProps> = ({
  paymentHistory,
  viewAdminNotes,
}) => {
  // State
  const [tooltipOpen, setToolTipOpen] = useState(false);

  const date = moment(paymentHistory.transactionDate);
  const userData = paymentHistory.user as IUser;
  const userName = getShortName(userData.firstname, userData.lastname, true);

  let planName = '-';

  const flexiblePlan = paymentHistory.flexiblePlan as IFlexiblePlan;
  const subscriptionPlan = paymentHistory.subscriptionPlan as ISubscriptionPlan;
  const invoiceData = paymentHistory.invoice as IInvoice;
  const courseData = paymentHistory.course as ICourse;

  if (flexiblePlan) {
    planName = flexiblePlan.name;
  } else if (subscriptionPlan) {
    planName = subscriptionPlan.name;
  } else if (invoiceData) {
    planName = `INV-${invoiceData.invoiceNumber.toString().padStart(4, '0')}`;
  } else if (paymentHistory.isCustomPlan) {
    planName = 'Custom Plan';
  } else if (courseData) {
    planName = `Course: ${courseData.title}`;
  } else if (paymentHistory.isPremiumUpgrade) {
    planName = 'Premium Upgrade';
  }

  const handleCopyReference = () => {
    setToolTipOpen(true);

    window.navigator.clipboard.writeText(paymentHistory.reference);

    setTimeout(() => {
      setToolTipOpen(false);
    }, 1000);
  };

  return (
    <StyledTableRow hover key={paymentHistory._id}>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {date.format('ddd MMM DD YYYY')}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {date.format('hh:mm A')}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {userName}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {planName}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {paymentHistory.method.toLocaleLowerCase().replace('_', ' ')}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {getFormatedDurationByMinutes({
            minutes: paymentHistory.planMinutes,
          })}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {paymentHistory.currency.toUpperCase()}{' '}
          {parseFloat((paymentHistory.amount / 100).toFixed(2))}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Badge
          badgeContent={(paymentHistory.adminNotes || []).length}
          color="primary"
        >
          <ButtonBase onClick={viewAdminNotes}>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              textTransform="capitalize"
              sx={{ textDecoration: 'underline' }}
            >
              View Admin Notes
            </Typography>
          </ButtonBase>
        </Badge>
      </StyledTableCell>
      <StyledTableCell>
        <Tooltip
          disableFocusListener
          disableHoverListener
          disableTouchListener
          open={tooltipOpen}
          title="Coped to clipboard"
          PopperProps={{
            disablePortal: true,
          }}
        >
          <ButtonBase onClick={handleCopyReference}>
            <Stack direction="row" alignItems="center">
              <ContentCopyIcon sx={{ fontSize: '12px' }} />
              &nbsp;
              <Typography
                fontSize="14px"
                fontWeight={500}
                color="#1E293B"
                textTransform="capitalize"
              >
                {paymentHistory.reference.slice(0, 15)}...
              </Typography>
            </Stack>
          </ButtonBase>
        </Tooltip>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default PaymentHistoryTableRow;
